import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { SupplierRequestType } from '@econtractor/types';
import EOverlay from 'components/others/EOverlay';
import ShareDocument from 'components/ShareLink';
import { GenerationLogEmailCell } from 'modules/templates';
import { UserNameItem } from 'modules/user';
import { escapeHtml } from 'utils/helpers/functions/escapeHtml';
import RequestActions from '../RequestActions';
import RequestStatusBadge from '../RequestStatusBadge';
import { SUPPLIER_REQUEST_STATUS } from '../model/constants';

const NOTE_OVERLAY_MAX_LENGTH = 80;

interface SupplierRequestsTableRowProps {
    row: SupplierRequestType;
    canOps: boolean;
    onOpen: (requestId: number) => void;
    onClone: (row: SupplierRequestType) => void;
    onChanged: () => void;
    onSendEmail: (row: SupplierRequestType) => void;
}

const SupplierRequestsTableRow = ({
    row,
    canOps,
    onOpen,
    onClone,
    onChanged,
    onSendEmail,
}: SupplierRequestsTableRowProps) => {
    const { t } = useTranslation(['Warehouses']);
    const dt = DT.fromMillis(Number(row.dt || 0));
    const fileUrl = String(row.filePublicUrl || '').trim();
    const note = String(row.note || '').trim();
    const notePreview =
        note.length > NOTE_OVERLAY_MAX_LENGTH
            ? `${note.slice(0, NOTE_OVERLAY_MAX_LENGTH).trimEnd()}…`
            : note;
    const noteHtml = notePreview ? `<div>${escapeHtml(notePreview)}</div>` : '';
    const vendorTitle = row.vendorName || `#${row.vendorId}`;
    const isDeleted = Number(row.status) === SUPPLIER_REQUEST_STATUS.DELETED;

    return (
        <tr>
            <td className="vendor" data-label={t('supplierRequests.colVendor')} title={vendorTitle}>
                <span
                    className="text-break cursor-pointer"
                    onClick={() => onOpen(Number(row.ID))}
                >
                    {vendorTitle}
                </span>
            </td>
            <td className="status" data-label={t('supplierRequests.colStatus')}>
                <RequestStatusBadge status={Number(row.status)} />
            </td>
            <td className="date" data-label={t('supplierRequests.colDate')}>
                <span className="d-block font-medium">{dt.toFormat('dd.MM.yyyy')}</span>
                <span className="text-muted">{dt.toFormat('HH:mm')}</span>
            </td>
            <td className="username" data-label={t('supplierRequests.colUser')}>
                <UserNameItem
                    user={{
                        userName: row.userName,
                        userEmail: row.userEmail,
                        userPicture: row.userPicture || undefined,
                    }}
                />
            </td>
            <td className="note" data-label={t('supplierRequests.colNote')}>
                <EOverlay text={noteHtml} />
            </td>
            <td className="email" data-label={t('supplierRequests.colEmail')}>
                <GenerationLogEmailCell
                    sentToEmail={row.sentToEmail}
                    sentEmail={row.sentEmail}
                    notSentLabel={t('supplierRequests.logNotSent')}
                    sentNoEmailLabel={t('supplierRequests.logSentNoEmail')}
                    sendTitle={t('supplierRequests.sendDialog.btnTitle')}
                    disabled={isDeleted || !canOps || !fileUrl}
                    onSend={canOps && !isDeleted ? () => onSendEmail(row) : undefined}
                />
            </td>
            <td className="file" data-label={t('supplierRequests.colFile')}>
                {fileUrl ? (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                        {t('supplierRequests.openPdf')}
                    </a>
                ) : (
                    <span className="text-muted">{t('supplierRequests.noFile')}</span>
                )}
            </td>
            <td className="action" data-label={t('supplierRequests.colActions')}>
                <div className="d-inline-flex align-items-center justify-content-end gap-1">
                    <ShareDocument
                        text={t('supplierRequests.shareText')}
                        link={fileUrl}
                        inputMessageDlg
                        disabled={isDeleted || !fileUrl}
                    />
                    <RequestActions
                        row={row}
                        canOps={canOps}
                        onOpen={onOpen}
                        onClone={onClone}
                        onChanged={onChanged}
                    />
                </div>
            </td>
        </tr>
    );
};

export default SupplierRequestsTableRow;
